import { NuvoList } from '@nuvolo/nuux-core/components/NuvoList';
import { useViewport } from '@nuvolo/nuux-theme/hooks/useViewport';
import React, { useCallback } from 'react';
import type { Asset, WorkOrderTemplate } from '#/graphql/types';
import If from '#/common/components/If';
import styled from 'styled-components';
import StandardThrobber from '../Throbbers/StandardThrobber';
import { color } from '@nuvolo/nuux-theme/utils';
import { borderRadius } from '#/common/hooks/useClientTheme';
import { BLANK_TEXT, executionIntervals } from '#/modules/wo-template/utils/constants';

export interface TemplatesListProps {
  loading: boolean;
  asset: Asset | undefined;
  templates: WorkOrderTemplate[];
  selectedTemplate?: WorkOrderTemplate;
  onSelect: (template: WorkOrderTemplate) => void;
}

const Styles = styled.div`
  .dx-list {
    .dx-scrollview-content {
      /* padding: 4px; */
    }
    .dx-item.dx-list-item {
      border: 1px solid ${color('ui', 'dark')};
      border-radius: ${borderRadius?.s};
      margin-bottom: 16px;

      &:last-child {
        margin-bottom: 0;
      }

      /* Hover and focused states background */
      &.dx-state-hover,
      &.dx-list-item-selected.dx-state-focused {
        background-color: ${color('custom', 'focusItemBackground')} !important;

        .item-container {
          background-color: ${color('custom', 'focusItemBackground')} !important;
        }
      }
    }
  }

  .dx-list-select-radiobutton {
    margin-right: 12px;
  }

  /* Lock radio button styles - prevent any parent state from changing them */
  .dx-item.dx-list-item {
    .dx-list-select-radiobutton-container,
    &.dx-state-hover .dx-list-select-radiobutton-container,
    &.dx-state-focused .dx-list-select-radiobutton-container,
    &.dx-list-item-selected .dx-list-select-radiobutton-container {
      .dx-radiobutton-icon {
        width: 20px;
        height: 20px;
        background-color: transparent !important;
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;

        &::before {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          border: 2px solid ${color('ui', 'dark')} !important;
          background-color: transparent !important;
          box-sizing: border-box;
        }

        &.dx-radiobutton-icon-checked .dx-radiobutton-icon-dot {
          width: 8px;
          height: 8px;
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          margin: 0;
          background-color: ${color('contrast', 'light')} !important;
          border-radius: 50%;
        }
      }
    }
  }

  .dx-list-select-radiobutton-container {
    width: 32px !important;
  }

  /* List item content styles */
  .item-container {
    padding: 6px !important;

    > div {
      > div:first-child {
        font-size: 16px;
        font-weight: 500;
        color: ${color('custom', 'contrastText')};
      }
      > div:nth-child(2) {
        font-size: 14px;
        font-weight: 400;
        color: ${color('custom', 'contrastText')};
      }
    }
  }

  /* Override focused/active state border */
  .dx-item.dx-state-focused:not(:active) .item-container,
  .dx-item.dx-list-item.dx-state-focused:not(.dx-state-active) .item-container {
    border-color: transparent !important;
  }
`;

const TemplatesList: React.FC<TemplatesListProps> = ({
  loading,
  asset,
  templates,
  selectedTemplate,
  onSelect,
}) => {
  const { height, size } = useViewport();
  const listHeight = Math.max(386, size === 'xs' ? height - 630 : height - 590);

  const onTemplateSelect = useCallback((t: WorkOrderTemplate[]) => onSelect(t[0]), [onSelect]);

  const templatesWithLabels = React.useMemo(
    () =>
      templates.map((t) => ({
        ...t,
        secondaryText:
          t.id === BLANK_TEXT
            ? t.description || '-'
            : executionIntervals.find((x) => x.key === t?.executionInterval)?.label || '-',
      })),
    [templates],
  );

  return (
    <>
      <If condition={loading}>
        <StandardThrobber />
      </If>
      <If condition={!loading}>
        <Styles>
          <NuvoList
            height={listHeight}
            dataSource={templatesWithLabels}
            includeImage={false}
            selectionMode="single"
            showSelectionControls={true}
            keyExpr="id"
            primaryTextExpr="title"
            secondaryTextExpr="secondaryText"
            selectedItemKeys={selectedTemplate?.id ? [selectedTemplate.id] : []}
            onSelectedItemsChange={onTemplateSelect}
            noDataText={asset ? 'No templates found' : 'Select an asset first'}
            focusStateEnabled={false}
            activeStateEnabled={false}
          />
        </Styles>
      </If>
    </>
  );
};

export default React.memo(TemplatesList);
